import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {AlertIcon} from '@primer/octicons-react'
import {Button, IconButton, Tooltip} from '@primer/react'
import {useRef} from 'react'

import {AlertsSidePanel} from './AlertsSidePanel'
import {useAnnotations, usePullRequestBasePath} from '../../stores/selectors'
import type {PageLimits} from '../../page-data/payloads/files'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'

export interface OpenAlertsPanelButtonProps {
  pageLimits: PageLimits
  endCommitOid: string
  className?: string
}

/**
 *
 * Renders a button that opens the alerts panel
 * Contains the total number of alerts plus the icon
 */
export function OpenAlertsPanelButton({pageLimits, endCommitOid, className}: OpenAlertsPanelButtonProps) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Alerts)

  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()

  const alerts = useAnnotations()
  const basePath = usePullRequestBasePath()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  if (!alerts) {
    return null
  }

  const totalAlertsCount = alerts.length
  const isSidePanelOpen = isDockedPanelOpen
  const alertsButtonAriaLabel = isDockedPanelOpen ? 'Close alerts panel' : 'Open alerts panel'

  // In docked mode, always render the panel so the dropdown can open the empty state.
  // The button itself still only shows when there are alerts.
  if (totalAlertsCount === 0) {
    return (
      <ErrorBoundary boundaryName="OpenAlertsPanelButton" fallback={null}>
        <AlertsSidePanel
          annotations={alerts}
          isOpen={isDockedPanelOpen}
          pageLimits={pageLimits}
          pathName={basePath}
          endCommitOid={endCommitOid}
          returnFocusRef={toggleSidePanelRef}
          onClose={() => actions.closePanel()}
        />
      </ErrorBoundary>
    )
  }

  return totalAlertsCount > 0 ? (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        boundaryName="OpenAlertsPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The alerts panel cannot currently be opened."
            icon={AlertIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <Tooltip ref={toggleSidePanelRef} text={alertsButtonAriaLabel} direction="sw" type="label">
          <Button
            className={className}
            aria-expanded={isSidePanelOpen}
            count={totalAlertsCount}
            leadingVisual={AlertIcon}
            size="small"
            variant="invisible"
            onClick={() => {
              sendPullRequestAnalyticsEvent('alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')
              actions.togglePanel('alerts', toggleSidePanelRef.current)
            }}
          />
        </Tooltip>
        <AlertsSidePanel
          annotations={alerts}
          isOpen={isDockedPanelOpen}
          pageLimits={pageLimits}
          pathName={basePath}
          endCommitOid={endCommitOid}
          returnFocusRef={toggleSidePanelRef}
          onClose={actions.closePanel}
        />
      </ErrorBoundary>
    </div>
  ) : null
}
